// Rounding & Notation Calculators helpers: significant figures, rounding to
// a multiple, ceiling/floor, truncation, engineering notation, order of
// magnitude, thousands-separator formatting, ordinal numbers, banker's
// rounding, decimal-place counting, cent rounding, and repeating-decimal
// notation. Basic decimal-place rounding, scientific notation, number base
// conversion, Roman numerals, and number-to-words already live in
// lib/tools/arithmeticCalculators.ts, lib/tools/powersRootsCalculators.ts,
// lib/tools/numberBaseConverter.ts, lib/tools/romanNumeralConverter.ts, and
// lib/tools/numberToWords.ts respectively - this file deliberately avoids
// re-implementing those.

// ============================================================
// 1. Significant Figures Calculator
// ============================================================
export function significantFiguresCalculator(params: { number: unknown; sig_figs: unknown }): { rounded_result: number } {
    const raw = Number(params.number) || 0
    const sigFigs = Math.max(Math.round(Number(params.sig_figs)) || 3, 1)
    if (raw === 0) return { rounded_result: 0 }
    const sign = raw < 0 ? -1 : 1
    const n = Math.abs(raw)
    const d = Math.ceil(Math.log10(n))
    const power = sigFigs - d
    const magnitude = Math.pow(10, power)
    return { rounded_result: (sign * Math.round(n * magnitude)) / magnitude }
}

// ============================================================
// 2. Round to Nearest Multiple Calculator
// ============================================================
export function roundToNearestMultipleCalculator(params: { number: unknown; multiple: unknown }): { rounded_result: number } {
    const n = Number(params.number) || 0
    const multiple = Number(params.multiple) || 1
    return { rounded_result: Math.round(n / multiple) * multiple }
}

// ============================================================
// 3. Ceiling and Floor Calculator
// ============================================================
export function ceilingFloorCalculator(params: { number: unknown; decimal_places: unknown }): { ceiling_result: number; floor_result: number } {
    const n = Number(params.number) || 0
    const d = Math.max(Math.round(Number(params.decimal_places)) || 0, 0)
    const factor = Math.pow(10, d)
    return { ceiling_result: Math.ceil(n * factor) / factor, floor_result: Math.floor(n * factor) / factor }
}

// ============================================================
// 4. Truncate Number Calculator
// ============================================================
export function truncateNumberCalculator(params: { number: unknown; decimal_places: unknown }): { truncated_result: number } {
    const n = Number(params.number) || 0
    const d = Math.max(Math.round(Number(params.decimal_places)) || 0, 0)
    const factor = Math.pow(10, d)
    return { truncated_result: Math.trunc(n * factor) / factor }
}

// ============================================================
// 5. Engineering Notation Calculator
// ============================================================
export function engineeringNotationCalculator(params: { number: unknown }): { coefficient: number; exponent: number; notation_display: string } {
    const raw = Number(params.number) || 0
    if (raw === 0) return { coefficient: 0, exponent: 0, notation_display: '0 × 10^0' }
    const sign = raw < 0 ? -1 : 1
    const n = Math.abs(raw)
    let exponent = Math.floor(Math.log10(n) / 3) * 3
    let coefficient = n / Math.pow(10, exponent)
    if (coefficient >= 1000) {
        coefficient /= 1000
        exponent += 3
    }
    if (coefficient < 1) {
        coefficient *= 1000
        exponent -= 3
    }
    coefficient *= sign
    return { coefficient, exponent, notation_display: `${coefficient} × 10^${exponent}` }
}

// ============================================================
// 6. Order of Magnitude Calculator
// ============================================================
export function orderOfMagnitudeCalculator(params: { number: unknown }): { order_of_magnitude: number; nearest_power_of_ten: number } {
    const n = Math.abs(Number(params.number)) || 0
    if (n === 0) return { order_of_magnitude: 0, nearest_power_of_ten: 1 }
    const order = Math.floor(Math.log10(n))
    return { order_of_magnitude: order, nearest_power_of_ten: Math.pow(10, order) }
}

// ============================================================
// 7. Number Formatter (Thousands Separator)
// ============================================================
export function numberFormatterCalculator(params: { number: unknown; separator_style: unknown; decimal_places: unknown }): { formatted_number: string } {
    const n = Number(params.number) || 0
    const style = String(params.separator_style || 'comma_period')
    const decimalPlaces = Math.max(Math.round(Number(params.decimal_places)) || 0, 0)
    const fixed = Math.abs(n).toFixed(decimalPlaces)
    const [intPart, decPart] = fixed.split('.')
    const groupedDigits = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    let thousands = ','
    let decimalSep = '.'
    if (style === 'period_comma') {
        thousands = '.'
        decimalSep = ','
    } else if (style === 'space_comma') {
        thousands = ' '
        decimalSep = ','
    } else if (style === 'space_period') {
        thousands = ' '
        decimalSep = '.'
    }

    let grouped = groupedDigits.split(',').join(thousands)
    const sign = n < 0 ? '-' : ''
    const formatted = decPart !== undefined ? `${sign}${grouped}${decimalSep}${decPart}` : `${sign}${grouped}`
    return { formatted_number: formatted }
}

// ============================================================
// 8. Ordinal Number Converter
// ============================================================
export function ordinalNumberConverter(params: { number: unknown; language: unknown }): { ordinal_result: string } {
    const n = Math.round(Number(params.number)) || 0
    const lang = String(params.language || 'en')

    switch (lang) {
        case 'ru':
            return { ordinal_result: `${n}-й` }
        case 'de':
            return { ordinal_result: `${n}.` }
        case 'es':
            return { ordinal_result: `${n}º` }
        case 'fr':
            return { ordinal_result: n === 1 ? '1er' : `${n}e` }
        case 'it':
            return { ordinal_result: `${n}°` }
        case 'pl':
            return { ordinal_result: `${n}.` }
        case 'lv':
            return { ordinal_result: `${n}.` }
        default: {
            const mod100 = Math.abs(n) % 100
            const mod10 = Math.abs(n) % 10
            let suffix = 'th'
            if (mod100 < 11 || mod100 > 13) {
                if (mod10 === 1) suffix = 'st'
                else if (mod10 === 2) suffix = 'nd'
                else if (mod10 === 3) suffix = 'rd'
            }
            return { ordinal_result: `${n}${suffix}` }
        }
    }
}

// ============================================================
// 9. Banker's Rounding Calculator (Round Half to Even)
// ============================================================
export function bankersRoundingCalculator(params: { number: unknown; decimal_places: unknown }): { rounded_result: number } {
    const n = Number(params.number) || 0
    const d = Math.max(Math.round(Number(params.decimal_places)) || 0, 0)
    const factor = Math.pow(10, d)
    const scaled = n * factor
    const flooredScaled = Math.floor(scaled)
    const fraction = scaled - flooredScaled

    let rounded: number
    if (Math.abs(fraction - 0.5) < 1e-9) {
        rounded = flooredScaled % 2 === 0 ? flooredScaled : flooredScaled + 1
    } else {
        rounded = Math.round(scaled)
    }
    return { rounded_result: rounded / factor }
}

// ============================================================
// 10. Decimal Places Counter
// ============================================================
export function decimalPlacesCounter(params: { number: unknown }): { decimal_place_count: number } {
    const str = String(params.number ?? '0')
    const dotIndex = str.indexOf('.')
    return { decimal_place_count: dotIndex === -1 ? 0 : str.length - dotIndex - 1 }
}

// ============================================================
// 11. Round to Nearest Cent Calculator
// ============================================================
export function roundToNearestCentCalculator(params: { amount: unknown }): { rounded_amount: number } {
    const amount = Number(params.amount) || 0
    return { rounded_amount: Math.round((amount + Number.EPSILON) * 100) / 100 }
}

// ============================================================
// 12. Repeating Decimal Notation Calculator
// ============================================================
export function repeatingDecimalCalculator(params: { numerator: unknown; denominator: unknown }): { decimal_notation: string } {
    let numerator = Math.round(Number(params.numerator)) || 0
    let denominator = Math.round(Number(params.denominator)) || 1
    const sign = numerator < 0 !== denominator < 0 && numerator !== 0 ? '-' : ''
    numerator = Math.abs(numerator)
    denominator = Math.abs(denominator) || 1

    const intPart = Math.floor(numerator / denominator)
    let remainder = numerator % denominator
    if (remainder === 0) return { decimal_notation: `${sign}${intPart}` }

    const seen = new Map<number, number>()
    let decimalDigits = ''
    const maxIterations = Math.min(denominator, 2000)
    let iterations = 0
    while (remainder !== 0 && !seen.has(remainder) && iterations < maxIterations) {
        seen.set(remainder, decimalDigits.length)
        remainder *= 10
        const digit = Math.floor(remainder / denominator)
        decimalDigits += digit
        remainder = remainder % denominator
        iterations++
    }

    if (remainder === 0) {
        return { decimal_notation: `${sign}${intPart}.${decimalDigits}` }
    }
    if (seen.has(remainder)) {
        const repeatStart = seen.get(remainder)!
        const nonRepeat = decimalDigits.slice(0, repeatStart)
        const repeat = decimalDigits.slice(repeatStart)
        return { decimal_notation: `${sign}${intPart}.${nonRepeat}(${repeat})` }
    }
    return { decimal_notation: `${sign}${intPart}.${decimalDigits}…` }
}
